
import { useState, useEffect, useRef } from 'react';
import { generateTTS } from '../services/geminiService';

export const useLessonAudio = (autoAdvance: boolean = true) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentUrlRef = useRef<string | null>(null);
  const playIdRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  const stop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current.onended = null;
      audioRef.current = null;
    }
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current);
      currentUrlRef.current = null;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setLoading(false);
  };

  const play = async (text: string, onComplete?: () => void) => {
    if (!text) return;
    stop();
    const currentPlayId = ++playIdRef.current;
    setLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      await new Promise(r => setTimeout(r, 50));
      if (currentPlayId !== playIdRef.current) return;

      const url = await generateTTS(text);
      if (controller.signal.aborted || currentPlayId !== playIdRef.current) return;

      if (url) {
        currentUrlRef.current = url;
        const audio = new Audio(url);
        audioRef.current = audio;
        
        audio.onended = () => {
          if (currentPlayId !== playIdRef.current) return;
          setIsPlaying(false);
          if (currentUrlRef.current === url) {
             URL.revokeObjectURL(url);
             currentUrlRef.current = null;
          }
          if (autoAdvance && onComplete) {
             setTimeout(() => {
                if (currentPlayId === playIdRef.current) onComplete();
             }, 800); 
          }
        };

        audio.onerror = (e) => {
          console.warn("[Audio] PCM play failed, fallback to Web Speech API", e);
          if (currentPlayId === playIdRef.current) playBrowserTTS(text, currentPlayId, onComplete);
        };

        await audio.play();
        if (currentPlayId === playIdRef.current) {
          setIsPlaying(true);
          setLoading(false);
        }
      } else {
        if (currentPlayId === playIdRef.current) playBrowserTTS(text, currentPlayId, onComplete);
      }
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      console.warn("[Audio] TTS logic failed", e);
      if (currentPlayId === playIdRef.current) playBrowserTTS(text, currentPlayId, onComplete);
    }
  };

  const playBrowserTTS = (text: string, playId: number, onComplete?: () => void) => {
    if (!window.speechSynthesis || playId !== playIdRef.current) {
      setLoading(false);
      return;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    
    // 关键修复：精确匹配 zh-CN 并且排除粤语(HK)和台语(TW)
    const preferredVoice = voices.find(v => 
      (v.lang.toLowerCase() === 'zh-cn' || v.lang.toLowerCase() === 'zh_cn' || v.name.includes('Mandarin')) &&
      !v.name.includes('Cantonese') && !v.name.includes('Hong Kong')
    ) || voices.find(v => v.lang.startsWith('zh')); // 最后才退而求其次选 zh

    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.rate = 1.0;
    utterance.pitch = 1.05;

    utterance.onstart = () => {
      if (playId === playIdRef.current) setIsPlaying(true);
    };

    utterance.onend = () => {
      if (playId !== playIdRef.current) return;
      setIsPlaying(false);
      setLoading(false);
      if (autoAdvance && onComplete) {
        setTimeout(onComplete, 800);
      }
    };

    utterance.onerror = () => {
      if (playId !== playIdRef.current) return;
      setIsPlaying(false);
      setLoading(false);
    };

    setLoading(false);
    window.speechSynthesis.speak(utterance);
  };

  return { isPlaying, loading, play, stop };
};
